import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { getStoredAuthType } from '../lib/captureInviteHash';
import type { User, Session } from '@supabase/supabase-js';

export type UserRole = 'owner' | 'admin' | 'editor' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: UserRole;
  isPlatformAdmin: boolean;
  needsPasswordSetup: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);

  const fetchRole = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('org_members')
        .select('role')
        .eq('user_id', userId)
        .limit(1)
        .single();
      setUserRole((data?.role as UserRole) ?? 'editor');
    } catch {
      setUserRole('owner');
    }

    try {
      const { data } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', userId)
        .limit(1)
        .single();
      setIsPlatformAdmin(!!data);
    } catch {
      setIsPlatformAdmin(false);
    }
  };

  /**
   * Check if the user needs to set a password. Uses session metadata first
   * (no network call). Only falls back to getUser() if the flag is set,
   * to get fresh data for the invite flow.
   */
  const checkPasswordFlag = (sessionUser: User): boolean => {
    console.log('[AuthCtx] checkPasswordFlag: user_metadata=', JSON.stringify(sessionUser.user_metadata ?? {}));
    if (sessionUser.user_metadata?.needs_password_setup === true) {
      console.log('[AuthCtx] checkPasswordFlag: needs_password_setup=true');
      return true;
    }
    const stored = getStoredAuthType();
    console.log('[AuthCtx] checkPasswordFlag: stored type=', stored?.type ?? 'none');
    if (stored?.type === 'invite') {
      console.log('[AuthCtx] checkPasswordFlag: invite from sessionStorage');
      return true;
    }
    console.log('[AuthCtx] checkPasswordFlag: returning false');
    return false;
  };

  useEffect(() => {
    console.log('[AuthCtx] registering onAuthStateChange');
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      try {
        console.log('[AuthCtx] event:', event, 'user:', newSession?.user?.email ?? 'null');
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          fetchRole(newSession.user.id);
        } else {
          setUserRole(null);
          setIsPlatformAdmin(false);
        }

        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          if (newSession?.user) {
            const needsPw = checkPasswordFlag(newSession.user);
            console.log('[AuthCtx] needsPasswordSetup=', needsPw);
            setNeedsPasswordSetup(needsPw);
          }
          setLoading(false);
          return;
        }

        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthCtx] PASSWORD_RECOVERY detected');
          setNeedsPasswordSetup(true);
          setLoading(false);
          return;
        }

        if (event === 'USER_UPDATED' && newSession?.user) {
          console.log('[AuthCtx] USER_UPDATED — rechecking flag');
          const needsPw = checkPasswordFlag(newSession.user);
          setNeedsPasswordSetup(needsPw);
        }

        if (event === 'SIGNED_OUT') {
          console.log('[AuthCtx] SIGNED_OUT');
          setNeedsPasswordSetup(false);
        }
      } catch (err) {
        console.error('[AuthCtx] onAuthStateChange error:', err);
      } finally {
        setLoading(false);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, userRole, isPlatformAdmin, needsPasswordSetup, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
